import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Search20Regular,
  BuildingFactory24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getWarehouseById,
  getWarehouses,
  type Warehouse as ApiWarehouse,
} from "../../armazens/armazensApi";

interface ArmazemView {
  id: number;
  nome: string;
  codigo?: string | null;
  morada?: string | null;
  responsavel?: string | null;
}

const formatOptional = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value.trim() ? value : "—";
  return String(value);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar armazéns.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os armazéns.";
};

const mapWarehouseToView = (warehouse: ApiWarehouse): ArmazemView => {
  return {
    id: warehouse.id,
    nome: warehouse.name,
    codigo: warehouse.code ?? null,
    morada: warehouse.address ?? null,
    responsavel: warehouse.responsible ?? null,
  };
};

const getInitials = (nome: string) => {
  return nome
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export function ArmazensView() {
  const navigate = useNavigate();
  const [armazens, setArmazens] = useState<ArmazemView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadWarehouses = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getWarehouses();
        if (!isMounted) return;

        const mapped = response.map(mapWarehouseToView);
        setArmazens(mapped);
        setSelectedWarehouseId((current) => {
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setArmazens([]);
        setSelectedWarehouseId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWarehouses();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredArmazens = useMemo(
    () =>
      armazens.filter((armazem) => {
        const matchesSearch =
          armazem.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (armazem.morada ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        return matchesSearch;
      }),
    [armazens, searchQuery],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredArmazens.length === 0) {
      setSelectedWarehouseId(null);
      return;
    }

    const hasSelection = filteredArmazens.some(
      (entry) => entry.id === selectedWarehouseId,
    );
    if (!hasSelection) {
      setSelectedWarehouseId(filteredArmazens[0].id);
    }
  }, [filteredArmazens, isLoading, selectedWarehouseId]);

  useEffect(() => {
    if (!selectedWarehouseId) return;

    let isMounted = true;

    const loadSelectedWarehouse = async () => {
      try {
        const response = await getWarehouseById(selectedWarehouseId);
        if (!isMounted) return;

        const mapped = mapWarehouseToView(response);
        setArmazens((current) =>
          current.map((armazem) =>
            armazem.id === selectedWarehouseId ? mapped : armazem,
          ),
        );
      } catch {
        // Keep list data if detail fetch fails.
      }
    };

    loadSelectedWarehouse();

    return () => {
      isMounted = false;
    };
  }, [selectedWarehouseId]);

  const selectedArmazem = filteredArmazens.find(
    (a) => a.id === selectedWarehouseId,
  );

  const handleWarehouseSelect = (warehouseId: number) => {
    setSelectedWarehouseId(warehouseId);
    setIsMobileDetailOpen(true);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/armazens/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Armazém
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Armazéns"
        subtitle="Lista de armazéns e dados de identificação"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar armazéns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar armazéns..."
                : `${filteredArmazens.length} armaz${
                    filteredArmazens.length !== 1 ? "éns" : "ém"
                  } encontrado${
                    filteredArmazens.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <BuildingFactory24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar armazéns...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <BuildingFactory24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredArmazens.map((armazem) => (
                <button
                  key={armazem.id}
                  onClick={() => handleWarehouseSelect(armazem.id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedWarehouseId === armazem.id ? "bg-accent/50" : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedWarehouseId === armazem.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedWarehouseId !== armazem.id) {
                      e.currentTarget.style.backgroundColor = "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedWarehouseId !== armazem.id) {
                      e.currentTarget.style.backgroundColor = "var(--card)";
                    }
                  }}
                >
                  {selectedWarehouseId === armazem.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    <div
                      className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(armazem.nome)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate"
                        style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        {armazem.nome}
                      </p>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(armazem.codigo)}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(armazem.morada)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading && !loadError && filteredArmazens.length === 0 && (
              <div className="p-8 text-center text-secondary-text">
                <p>Nenhum armazém encontrado</p>
              </div>
            )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedArmazem ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <BuildingFactory24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um armazém para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-large)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(selectedArmazem.nome)}
                    </div>

                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight: "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedArmazem.nome}
                      </h3>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={() =>
                        navigate(`/armazens/editar/${selectedArmazem.id}`)
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Armazém</span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Dados do Armazém
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Identificador Interno
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedArmazem.codigo)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Responsável
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedArmazem.responsavel)}</p>
                  </div>

                  <div className="sm:col-span-2">
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Morada
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedArmazem.morada)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
